import { JsonObject, JsonProperty, Any } from 'json2typescript';
import { ModelObject } from '@hermes/api-model-core';
import { OptinCommunication } from '@hermes/api-model-account';
import { Fapiao, Ecpay, CfdiProperties, TaxInvoice } from '@hermes/api-model-basket';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * This class represents an Apple Pay session
 * https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/requesting_an_apple_pay_payment_session
 * "..., your server receives an opaque Apple Pay session object"
 * The object is a json, but no property is to be documented
 */
let ApplePaySession = class ApplePaySession extends ModelObject {
};
ApplePaySession = __decorate([
    JsonObject("ApplePaySession")
], ApplePaySession);

let BrowserData = class BrowserData extends ModelObject {
    constructor() {
        super(...arguments);
        this.colorDepth = undefined;
        this.language = undefined;
        this.screenHeight = undefined;
        this.screenWidth = undefined;
        this.timeZoneOffset = undefined;
    }
};
__decorate([
    JsonProperty("color_depth", Number, true),
    __metadata("design:type", Number)
], BrowserData.prototype, "colorDepth", undefined);
__decorate([
    JsonProperty("language", String, true),
    __metadata("design:type", String)
], BrowserData.prototype, "language", undefined);
__decorate([
    JsonProperty("screen_height", Number, true),
    __metadata("design:type", Number)
], BrowserData.prototype, "screenHeight", undefined);
__decorate([
    JsonProperty("screen_width", Number, true),
    __metadata("design:type", Number)
], BrowserData.prototype, "screenWidth", undefined);
__decorate([
    JsonProperty("time_zone_offset", Number, true),
    __metadata("design:type", Number)
], BrowserData.prototype, "timeZoneOffset", undefined);
BrowserData = __decorate([
    JsonObject("BrowserData")
], BrowserData);

let CardDetails = class CardDetails {
    constructor() {
        this.expiryMonth = undefined;
        this.expiryYear = undefined;
        this.holderName = undefined;
        this.endNumbers = undefined;
    }
};
__decorate([
    JsonProperty("expiryMonth", String, false),
    __metadata("design:type", String)
], CardDetails.prototype, "expiryMonth", undefined);
__decorate([
    JsonProperty("expiryYear", String, false),
    __metadata("design:type", String)
], CardDetails.prototype, "expiryYear", undefined);
__decorate([
    JsonProperty("holderName", String, false),
    __metadata("design:type", String)
], CardDetails.prototype, "holderName", undefined);
__decorate([
    JsonProperty("endNumbers", String, false),
    __metadata("design:type", String)
], CardDetails.prototype, "endNumbers", undefined);
CardDetails = __decorate([
    JsonObject("CardDetails")
], CardDetails);

let Card = class Card {
    constructor() {
        this.id = undefined;
        this.card = undefined;
        this.contractTypes = undefined;
        this.creationDate = undefined;
        this.cardToken = undefined;
        this.cardType = undefined;
        this.lastUsedTime = undefined;
    }
};
__decorate([
    JsonProperty("id", String, false),
    __metadata("design:type", String)
], Card.prototype, "id", undefined);
__decorate([
    JsonProperty("card", CardDetails, false),
    __metadata("design:type", CardDetails)
], Card.prototype, "card", undefined);
__decorate([
    JsonProperty("contractTypes", [String], false),
    __metadata("design:type", Array)
], Card.prototype, "contractTypes", undefined);
__decorate([
    JsonProperty("creationDate", String, false),
    __metadata("design:type", String)
], Card.prototype, "creationDate", undefined);
__decorate([
    JsonProperty("cardToken", String, false),
    __metadata("design:type", String)
], Card.prototype, "cardToken", undefined);
__decorate([
    JsonProperty("cardType", String, false),
    __metadata("design:type", String)
], Card.prototype, "cardType", undefined);
__decorate([
    JsonProperty("lastUsedTime", String, false),
    __metadata("design:type", String)
], Card.prototype, "lastUsedTime", undefined);
Card = __decorate([
    JsonObject("Card")
], Card);

let Cards = class Cards {
    constructor() {
        this.recurringContracts = undefined;
    }
};
__decorate([
    JsonProperty("recurringContracts", [Card], true),
    __metadata("design:type", Array)
], Cards.prototype, "recurringContracts", undefined);
Cards = __decorate([
    JsonObject("Cards")
], Cards);

let PaymentDataRequest = class PaymentDataRequest {
};
PaymentDataRequest = __decorate([
    JsonObject("PaymentDataRequest")
], PaymentDataRequest);

let PaymentRedirect = class PaymentRedirect extends ModelObject {
    constructor() {
        super(...arguments);
        this.method = undefined;
        this.url = undefined;
        this.data = undefined;
    }
};
__decorate([
    JsonProperty("method", String, true),
    __metadata("design:type", String)
], PaymentRedirect.prototype, "method", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], PaymentRedirect.prototype, "url", undefined);
__decorate([
    JsonProperty("data", Any, true),
    __metadata("design:type", Object)
], PaymentRedirect.prototype, "data", undefined);
PaymentRedirect = __decorate([
    JsonObject("PaymentRedirect")
], PaymentRedirect);

let TrackingInformation = class TrackingInformation {
};
__decorate([
    JsonProperty("feature", String),
    __metadata("design:type", String)
], TrackingInformation.prototype, "feature", undefined);
__decorate([
    JsonProperty("origin", String),
    __metadata("design:type", String)
], TrackingInformation.prototype, "origin", undefined);
__decorate([
    JsonProperty("information", String),
    __metadata("design:type", String)
], TrackingInformation.prototype, "information", undefined);
TrackingInformation = __decorate([
    JsonObject("TrackingInformation")
], TrackingInformation);
var FeatureType;
(function (FeatureType) {
    FeatureType["BACK_IN_STOCK"] = "back_in_stock";
})(FeatureType || (FeatureType = {}));
var CustomerOrigin;
(function (CustomerOrigin) {
    CustomerOrigin["EMAIL"] = "email";
    CustomerOrigin["UNKNOWN"] = "unknown";
})(CustomerOrigin || (CustomerOrigin = {}));

let PaymentRequest = class PaymentRequest {
    constructor() {
        this.optInCommunication = undefined;
    }
};
__decorate([
    JsonProperty("no_origin_key", Number, true),
    __metadata("design:type", Number)
], PaymentRequest.prototype, "noOriginKey", undefined);
__decorate([
    JsonProperty("payment_method", String),
    __metadata("design:type", String)
], PaymentRequest.prototype, "payment_method", undefined);
__decorate([
    JsonProperty("known_but_not_connected_email", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "knownButNotConnectedEmail", undefined);
__decorate([
    JsonProperty("user_agent", String),
    __metadata("design:type", String)
], PaymentRequest.prototype, "userAgent", undefined);
__decorate([
    JsonProperty("locale", String),
    __metadata("design:type", String)
], PaymentRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("dob", Date, true),
    __metadata("design:type", Date)
], PaymentRequest.prototype, "dob", undefined);
__decorate([
    JsonProperty("newsletter", Boolean, true),
    __metadata("design:type", Boolean)
], PaymentRequest.prototype, "newsletter", undefined);
__decorate([
    JsonProperty("url_redirect", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "url_redirect", undefined);
__decorate([
    JsonProperty("payment_data", PaymentDataRequest, true),
    __metadata("design:type", PaymentDataRequest)
], PaymentRequest.prototype, "payment_data", undefined);
__decorate([
    JsonProperty("payment_device", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "payment_device", undefined);
__decorate([
    JsonProperty("fapiao", Fapiao, true),
    __metadata("design:type", Fapiao)
], PaymentRequest.prototype, "fapiao", undefined);
__decorate([
    JsonProperty("ecpay", Ecpay, true),
    __metadata("design:type", Ecpay)
], PaymentRequest.prototype, "ecpay", undefined);
__decorate([
    JsonProperty("cfdi", CfdiProperties, true),
    __metadata("design:type", CfdiProperties)
], PaymentRequest.prototype, "cfdi", undefined);
__decorate([
    JsonProperty("payment_handler", Boolean, true),
    __metadata("design:type", Boolean)
], PaymentRequest.prototype, "payment_handler", undefined);
__decorate([
    JsonProperty("openid", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "openid", undefined);
__decorate([
    JsonProperty("tax_invoice", TaxInvoice, true),
    __metadata("design:type", TaxInvoice)
], PaymentRequest.prototype, "taxInvoice", undefined);
__decorate([
    JsonProperty("social_security_number", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "socialSecurityNumber", undefined);
__decorate([
    JsonProperty("communicationConsent", Boolean, true),
    __metadata("design:type", Boolean)
], PaymentRequest.prototype, "communicationConsent", undefined);
__decorate([
    JsonProperty("paypal_sub_type", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "paypalSubtype", undefined);
__decorate([
    JsonProperty("browser_info", BrowserData, true),
    __metadata("design:type", BrowserData)
], PaymentRequest.prototype, "browserInfo", undefined);
__decorate([
    JsonProperty("processing_channel", String, true),
    __metadata("design:type", String)
], PaymentRequest.prototype, "processingChannel", undefined);
__decorate([
    JsonProperty("tracking", TrackingInformation, true),
    __metadata("design:type", TrackingInformation)
], PaymentRequest.prototype, "trackingInformation", undefined);
__decorate([
    JsonProperty("optInCommunication", OptinCommunication, true),
    __metadata("design:type", OptinCommunication)
], PaymentRequest.prototype, "optInCommunication", undefined);
__decorate([
    JsonProperty("pipl", Boolean, true),
    __metadata("design:type", Boolean)
], PaymentRequest.prototype, "pipl", undefined);
PaymentRequest = __decorate([
    JsonObject("PaymentRequest")
], PaymentRequest);

let PaymentSdk = class PaymentSdk extends ModelObject {
    constructor() {
        super(...arguments);
        this.orderId = undefined;
        this.clientSecret = undefined;
    }
};
__decorate([
    JsonProperty("orderId", String, true),
    __metadata("design:type", String)
], PaymentSdk.prototype, "orderId", undefined);
__decorate([
    JsonProperty("clientSecret", String, true),
    __metadata("design:type", String)
], PaymentSdk.prototype, "clientSecret", undefined);
PaymentSdk = __decorate([
    JsonObject("PaymentSdk")
], PaymentSdk);

let PaymentThreeDS2 = class PaymentThreeDS2 extends ModelObject {
    constructor() {
        super(...arguments);
        this.url = undefined;
        this.paymentData = undefined;
        this.token = undefined;
        this.type = undefined;
        this.paymentMethodType = undefined;
        this.authorisationToken = undefined;
        this.subtype = undefined;
    }
};
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "url", undefined);
__decorate([
    JsonProperty("payment_data", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "paymentData", undefined);
__decorate([
    JsonProperty("token", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "token", undefined);
__decorate([
    JsonProperty("type", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "type", undefined);
__decorate([
    JsonProperty("payment_method_type", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "paymentMethodType", undefined);
__decorate([
    JsonProperty("authorisation_token", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "authorisationToken", undefined);
__decorate([
    JsonProperty("subtype", String, true),
    __metadata("design:type", String)
], PaymentThreeDS2.prototype, "subtype", undefined);
PaymentThreeDS2 = __decorate([
    JsonObject("PaymentThreeDS2")
], PaymentThreeDS2);

let PaymentResponse = class PaymentResponse extends ModelObject {
    constructor() {
        super(...arguments);
        this.paymentType = undefined;
        this.paymentRedirect = undefined;
        this.formAction = undefined;
        this.formFields = undefined;
        this.code = undefined;
        this.internalCode = undefined;
        this.message = undefined;
        this.orderId = undefined;
        this.amount = undefined;
        this.currency = undefined;
        this.successes = undefined;
        this.mercuryRedirect = undefined;
        this.mercuryThreeDS2 = undefined;
        this.expirationDate = undefined;
        this.method = undefined;
        this.mercurySdk = undefined;
    }
};
__decorate([
    JsonProperty("payment_type", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "paymentType", undefined);
__decorate([
    JsonProperty("payment_redirect", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "paymentRedirect", undefined);
__decorate([
    JsonProperty("form_action", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "formAction", undefined);
__decorate([
    JsonProperty("form_fields", Any, true),
    __metadata("design:type", Object)
], PaymentResponse.prototype, "formFields", undefined);
__decorate([
    JsonProperty("code", Any, true),
    __metadata("design:type", Object)
], PaymentResponse.prototype, "code", undefined);
__decorate([
    JsonProperty("internal_code", Any, true),
    __metadata("design:type", Object)
], PaymentResponse.prototype, "internalCode", undefined);
__decorate([
    JsonProperty("message", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "message", undefined);
__decorate([
    JsonProperty("order_id", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "orderId", undefined);
__decorate([
    JsonProperty("amount", Number, true),
    __metadata("design:type", Number)
], PaymentResponse.prototype, "amount", undefined);
__decorate([
    JsonProperty("currency", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "currency", undefined);
__decorate([
    JsonProperty("successes", Any, true),
    __metadata("design:type", Object)
], PaymentResponse.prototype, "successes", undefined);
__decorate([
    JsonProperty("mercury_redirect", PaymentRedirect, true),
    __metadata("design:type", PaymentRedirect)
], PaymentResponse.prototype, "mercuryRedirect", undefined);
__decorate([
    JsonProperty("mercuryThreeDS2", PaymentThreeDS2, true),
    __metadata("design:type", PaymentThreeDS2)
], PaymentResponse.prototype, "mercuryThreeDS2", undefined);
__decorate([
    JsonProperty("expiration_date", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "expirationDate", undefined);
__decorate([
    JsonProperty("method", String, true),
    __metadata("design:type", String)
], PaymentResponse.prototype, "method", undefined);
__decorate([
    JsonProperty("mercury_sdk", PaymentSdk, true),
    __metadata("design:type", PaymentSdk)
], PaymentResponse.prototype, "mercurySdk", undefined);
PaymentResponse = __decorate([
    JsonObject("PaymentResponse")
], PaymentResponse);

let PaymentStatusRequest = class PaymentStatusRequest {
    constructor() {
        this.locale = undefined;
        this.timestamp = undefined;
    }
};
__decorate([
    JsonProperty("locale", String, false),
    __metadata("design:type", String)
], PaymentStatusRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("timestamp", Number, false),
    __metadata("design:type", Number)
], PaymentStatusRequest.prototype, "timestamp", undefined);
PaymentStatusRequest = __decorate([
    JsonObject("PaymentStatusRequest")
], PaymentStatusRequest);

let PaymentValidationResponse = class PaymentValidationResponse extends ModelObject {
    constructor() {
        super(...arguments);
        this.orderId = undefined;
        this.message = undefined;
    }
};
__decorate([
    JsonProperty("validation_status", String, true),
    __metadata("design:type", String)
], PaymentValidationResponse.prototype, "validationStatus", undefined);
__decorate([
    JsonProperty("order_id", String, true),
    __metadata("design:type", String)
], PaymentValidationResponse.prototype, "orderId", undefined);
__decorate([
    JsonProperty("message", String, true),
    __metadata("design:type", String)
], PaymentValidationResponse.prototype, "message", undefined);
PaymentValidationResponse = __decorate([
    JsonObject("PaymentValidationResponse")
], PaymentValidationResponse);

class Payment extends ModelObject {
    constructor(code, name) {
        super();
        this.code = code;
        this.name = name;
    }
}

let TaxEstimationResponse = class TaxEstimationResponse {
    constructor() {
        this.prices = undefined;
        this.taxes = undefined;
        this.availableShippingMethods = undefined;
        this.avalaraError = undefined;
        this.deliveryError = undefined;
    }
};
__decorate([
    JsonProperty("prices", Any, true)
    /** Map of prices per product Key(SKU) => Value(Price) */
    ,
    __metadata("design:type", Object)
], TaxEstimationResponse.prototype, "prices", undefined);
__decorate([
    JsonProperty("taxes", Number),
    __metadata("design:type", Number)
], TaxEstimationResponse.prototype, "taxes", undefined);
__decorate([
    JsonProperty("available_shipping_methods", Any),
    __metadata("design:type", Array)
], TaxEstimationResponse.prototype, "availableShippingMethods", undefined);
__decorate([
    JsonProperty("avalara_error", String, true),
    __metadata("design:type", String)
], TaxEstimationResponse.prototype, "avalaraError", undefined);
__decorate([
    JsonProperty("delivery_error", String, true),
    __metadata("design:type", String)
], TaxEstimationResponse.prototype, "deliveryError", undefined);
TaxEstimationResponse = __decorate([
    JsonObject("TaxEstimationResponse")
], TaxEstimationResponse);

let WechatPaymentCheckRequest = class WechatPaymentCheckRequest {
    constructor() {
        this.orderId = undefined;
        this.locale = undefined;
        this.timestamp = undefined;
    }
};
__decorate([
    JsonProperty("orderId", String, true),
    __metadata("design:type", String)
], WechatPaymentCheckRequest.prototype, "orderId", undefined);
__decorate([
    JsonProperty("locale", String, true),
    __metadata("design:type", String)
], WechatPaymentCheckRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("timestamp", Number, true),
    __metadata("design:type", Number)
], WechatPaymentCheckRequest.prototype, "timestamp", undefined);
WechatPaymentCheckRequest = __decorate([
    JsonObject("WechatPaymentCheckRequest")
], WechatPaymentCheckRequest);

let WechatPaymentCheckResponse = class WechatPaymentCheckResponse {
    constructor() {
        this.orderPaid = undefined;
    }
};
__decorate([
    JsonProperty("order_paid", Boolean, true),
    __metadata("design:type", Boolean)
], WechatPaymentCheckResponse.prototype, "orderPaid", undefined);
WechatPaymentCheckResponse = __decorate([
    JsonObject("WechatPaymentCheckResponse")
], WechatPaymentCheckResponse);

let Wechatpay = class Wechatpay extends ModelObject {
    constructor() {
        super(...arguments);
        this.codeUrl = undefined;
        this.qrcodePage = undefined;
        this.shortPollingInterval = undefined;
        this.maxCheckPayment = undefined;
        this.orderId = undefined;
        this.weChatUrl = undefined;
        this.orderAmount = undefined;
    }
};
__decorate([
    JsonProperty("code_url", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "codeUrl", undefined);
__decorate([
    JsonProperty("qrcode_page", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "qrcodePage", undefined);
__decorate([
    JsonProperty("short_pooling_interval", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "shortPollingInterval", undefined);
__decorate([
    JsonProperty("max_check_payment", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "maxCheckPayment", undefined);
__decorate([
    JsonProperty("order_id", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "orderId", undefined);
__decorate([
    JsonProperty("mweb_url", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "weChatUrl", undefined);
__decorate([
    JsonProperty("order_amount", String, true),
    __metadata("design:type", String)
], Wechatpay.prototype, "orderAmount", undefined);
Wechatpay = __decorate([
    JsonObject("Wechatpay")
], Wechatpay);

export { ApplePaySession, BrowserData, Card, CardDetails, Cards, CustomerOrigin, FeatureType, Payment, PaymentDataRequest, PaymentRedirect, PaymentRequest, PaymentResponse, PaymentSdk, PaymentStatusRequest, PaymentThreeDS2, PaymentValidationResponse, TaxEstimationResponse, TrackingInformation, WechatPaymentCheckRequest, WechatPaymentCheckResponse, Wechatpay };
